import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const CouplerContainer = styled.section`
  min-height: 100vh;
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #1e40af 0%, #3730a3 50%, #581c87 100%);
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const TypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const TypeCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
`;

const TypeTitle = styled.h3`
  font-size: 1.5rem;
  color: #3b82f6;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const TypeDescription = styled.p`
  color: #94a3b8;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const TypeSpecs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const Spec = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 10px;
  text-align: center;
`;

const SpecLabel = styled.div`
  color: #64748b;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const SpecValue = styled.div`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
`;

function CouplerTypes() {
  const [selectedType, setSelectedType] = useState(null);

  const couplerTypes = [
    {
      id: 'threaded',
      title: 'Threaded Couplers',
      description: 'Most common type using threaded connections for reliable performance',
      specs: {
        strength: '125%',
        speed: '15-20/hr',
        cost: '₹35',
        applications: 'High-rise, Bridges'
      }
    },
    {
      id: 'swaged',
      title: 'Swaged Couplers',
      description: 'Crimped connections for fast installation and consistent quality',
      specs: {
        strength: '100%',
        speed: '20-25/hr',
        cost: '₹28',
        applications: 'Precast, Mass Construction'
      }
    },
    {
      id: 'grout',
      title: 'Grout-Filled Couplers',
      description: 'Sleeve couplers filled with high-strength grout for durability',
      specs: {
        strength: '110%',
        speed: '12-15/hr',
        cost: '₹42',
        applications: 'Seismic, Critical Structures'
      }
    },
    {
      id: 'adhesive',
      title: 'Adhesive Couplers',
      description: 'Bonded connections using structural adhesives for flexibility',
      specs: {
        strength: '95%',
        speed: '8-12/hr',
        cost: '₹55',
        applications: 'Retrofit, Special Conditions'
      }
    }
  ];

  return (
    <CouplerContainer id="coupler-types" data-section="coupler-types">
      <Content>
        <SectionTitle
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Coupler Types & Specifications
        </SectionTitle>

        <TypesGrid>
          {couplerTypes.map((type, index) => (
            <TypeCard
              key={type.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => setSelectedType(type)}
            >
              <TypeTitle>{type.title}</TypeTitle>
              <TypeDescription>{type.description}</TypeDescription>
              
              <TypeSpecs>
                <Spec>
                  <SpecLabel>Strength</SpecLabel>
                  <SpecValue>{type.specs.strength}</SpecValue>
                </Spec>
                <Spec>
                  <SpecLabel>Speed</SpecLabel>
                  <SpecValue>{type.specs.speed}</SpecValue>
                </Spec>
                <Spec>
                  <SpecLabel>Cost</SpecLabel>
                  <SpecValue>{type.specs.cost}</SpecValue>
                </Spec>
                <Spec>
                  <SpecLabel>Applications</SpecLabel>
                  <SpecValue>{type.specs.applications}</SpecValue>
                </Spec>
              </TypeSpecs>
            </TypeCard>
          ))}
        </TypesGrid>
      </Content>
    </CouplerContainer>
  );
}

export default CouplerTypes;